import { api } from "@web/lib/axios";
import { useCallback, useEffect, useRef, useState } from "react";
import type Webcam from "react-webcam";

export type SealCaptureGuidance =
	| "idle"
	| "find"
	| "detected"
	| "closer"
	| "farther"
	| "center"
	| "hold"
	| "capturing";

export type SealCaptureBBox = {
	cx: number;
	cy: number;
	w: number;
	h: number;
};

type InferResponse = {
	detected: boolean;
	guidance: Exclude<SealCaptureGuidance, "idle" | "capturing">;
	shouldCapture: boolean;
	accepted: boolean;
	bbox?: SealCaptureBBox;
	serverMs: number;
};

type UseSealCaptureGuidanceProps = {
	webcamRef: React.RefObject<Webcam | null>;
	enabled: boolean;
	onFinalCapture: (image: Blob) => Promise<void> | void;
};

const GUIDE_INTERVAL_MS = 330;
const GUIDE_MAX_WIDTH = 640;
const FINAL_JPEG_QUALITY = 0.98;
const DETECTION_HOLD_MS = 2_000;

const frameToBlob = async (
	video: HTMLVideoElement,
	maxWidth: number,
	quality: number,
) => {
	const sourceWidth = video.videoWidth;
	const sourceHeight = video.videoHeight;
	const scale = Math.min(1, maxWidth / sourceWidth);
	const width = Math.max(1, Math.round(sourceWidth * scale));
	const height = Math.max(1, Math.round(sourceHeight * scale));
	const canvas = document.createElement("canvas");

	canvas.width = width;
	canvas.height = height;

	const context = canvas.getContext("2d", { alpha: false });
	if (!context) throw new Error("Canvas unavailable");

	context.drawImage(video, 0, 0, width, height);

	return new Promise<Blob>((resolve, reject) => {
		canvas.toBlob(
			(blob) =>
				blob ? resolve(blob) : reject(new Error("Could not encode frame")),
			"image/jpeg",
			quality,
		);
	});
};

export function useSealCaptureGuidance({
	webcamRef,
	enabled,
	onFinalCapture,
}: UseSealCaptureGuidanceProps) {
	const inFlightRef = useRef(false);
	const detectedSinceRef = useRef<number | null>(null);
	const finalCaptureRef = useRef(false);
	const requestAbortRef = useRef<AbortController | null>(null);
	const onFinalCaptureRef = useRef(onFinalCapture);
	const [guidance, setGuidance] = useState<SealCaptureGuidance>("idle");
	const [countdown, setCountdown] = useState<number | null>(null);

	useEffect(() => {
		onFinalCaptureRef.current = onFinalCapture;
	}, [onFinalCapture]);

	const infer = useCallback(async (image: Blob, mode: "guide" | "final") => {
		requestAbortRef.current?.abort();
		const controller = new AbortController();
		requestAbortRef.current = controller;

		const form = new FormData();
		form.append(
			"image",
			image,
			mode === "final" ? "seal-final.jpg" : "seal-guide.jpg",
		);
		form.append("mode", mode);

		try {
			const { data } = await api.post<InferResponse>(
				"/seal-capture/infer",
				form,
				{
					signal: controller.signal,
				},
			);
			return data;
		} finally {
			if (requestAbortRef.current === controller) {
				requestAbortRef.current = null;
			}
		}
	}, []);

	const reset = useCallback(() => {
		requestAbortRef.current?.abort();
		requestAbortRef.current = null;
		inFlightRef.current = false;
		detectedSinceRef.current = null;
		finalCaptureRef.current = false;
		setCountdown(null);
		setGuidance(enabled ? "find" : "idle");
	}, [enabled]);

	const captureFinal = useCallback(
		async (video: HTMLVideoElement) => {
			if (finalCaptureRef.current) return;

			finalCaptureRef.current = true;
			setGuidance("capturing");
			setCountdown(null);

			try {
				const image = await frameToBlob(
					video,
					video.videoWidth,
					FINAL_JPEG_QUALITY,
				);
				const result = await infer(image, "final");

				if (result.accepted) {
					await onFinalCaptureRef.current(image);
					return;
				}

				detectedSinceRef.current = null;
				finalCaptureRef.current = false;
				setGuidance(result.guidance ?? "find");
				setCountdown(null);
			} catch {
				detectedSinceRef.current = null;
				finalCaptureRef.current = false;
				setGuidance("find");
			}
		},
		[infer],
	);

	const runGuideInference = useCallback(async () => {
		const video = webcamRef.current?.video;
		if (
			!enabled ||
			!video ||
			video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA ||
			inFlightRef.current ||
			finalCaptureRef.current
		) {
			return;
		}

		inFlightRef.current = true;

		try {
			const image = await frameToBlob(video, GUIDE_MAX_WIDTH, 0.68);
			const result = await infer(image, "guide");
			setGuidance(result.detected ? "detected" : result.guidance);

			if (!result.detected) {
				setCountdown(null);
				detectedSinceRef.current = null;
				return;
			}

			const now = performance.now();
			detectedSinceRef.current ??= now;
			if (now - detectedSinceRef.current >= DETECTION_HOLD_MS) {
				await captureFinal(video);
			} else {
				setCountdown(
					Math.ceil(
						(DETECTION_HOLD_MS - (now - detectedSinceRef.current)) / 1_000,
					),
				);
			}
		} catch {
			detectedSinceRef.current = null;
			setGuidance("find");
			setCountdown(null);
		} finally {
			inFlightRef.current = false;
		}
	}, [captureFinal, enabled, infer, webcamRef]);

	useEffect(() => {
		if (!enabled) {
			reset();
			return;
		}

		reset();
		const timer = window.setInterval(() => {
			if (detectedSinceRef.current !== null && !finalCaptureRef.current) {
				setCountdown(
					Math.max(
						1,
						Math.ceil(
							(DETECTION_HOLD_MS -
								(performance.now() - detectedSinceRef.current)) /
								1_000,
						),
					),
				);
			}
			void runGuideInference();
		}, GUIDE_INTERVAL_MS);

		return () => {
			window.clearInterval(timer);
			requestAbortRef.current?.abort();
			requestAbortRef.current = null;
		};
	}, [enabled, reset, runGuideInference]);

	return { guidance, countdown, reset };
}
